import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  Icon,
  Box,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Text,
  Heading,
} from '@chakra-ui/react';
import {
  CheckCircleIcon,
  WarningIcon,
  TimeIcon,
  InfoIcon,
  TriangleUpIcon,
  TriangleDownIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import ModalLicenciaVinosLicores from './ModalLicenciaVinosLicores';
import ModalHistorialVinosLicores from './ModalHistorialVinosLicores';
import { FaClock, FaPrint, FaReceipt, FaFilePdf, FaFileExcel, FaWineBottle } from 'react-icons/fa';
import FloatingAddButton from '../../../components/FloatingAddButton';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}`;

interface LicenciaVinosLicores {
  Id: number;
  usuarioId?: number;
  folioLicencia?: string;
  ciudadano?: string;
  nombreEstablecimiento?: string;
  giroVinosLicoresId?: number;
  giroVinosLicores?: {
    codigo: string;
    descripcion: string;
  };
  tipo?: string;
  estatus?: string;
  direccion?: string;
  localidad?: string;
  calle?: string;
  numero?: string;
  codigoPostal?: string;
  horario?: string;
  creadoEn?: string;
  montoLicencia?: number;
  descripcion?: string;
}

const TablaLicenciasVinosLicores = () => {
  const [licencias, setLicencias] = useState<LicenciaVinosLicores[]>([]);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina] = useState(50);
  const [sortConfig, setSortConfig] = useState<{ key: keyof LicenciaVinosLicores; direction: 'asc' | 'desc' } | null>(null);

  const formatearFecha = (fechaISO: string) => {
    if (!fechaISO) return '—';
    const [año, mes, dia] = fechaISO.split('T')[0].split('-');
    return `${dia}/${mes}/${año}`;
  };

  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isHistorialOpen, onOpen: onHistorialOpen, onClose: onHistorialClose } = useDisclosure();
  const [licenciaSeleccionada, setLicenciaSeleccionada] = useState<LicenciaVinosLicores | null>(null);
  const [licenciaIdHistorial, setLicenciaIdHistorial] = useState<number | null>(null);
  const [modoModal, setModoModal] = useState<'view' | 'edit' | 'create'>('view');

  useEffect(() => {
    const fetchLicencias = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores`);
        const data = await res.json();
        setLicencias(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error al cargar licencias de vinos y licores:', error);
        setLicencias([]);
      }
    };

    fetchLicencias();
  }, []);

  const requestSort = (key: keyof LicenciaVinosLicores) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig?.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof LicenciaVinosLicores) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <TriangleUpIcon ml={1} /> : <TriangleDownIcon ml={1} />;
  };

  const getEstatusColor = (estatus: string) => {
    switch (estatus) {
      case 'Activa':
        return 'green';
      case 'Pendiente':
        return 'orange';
      case 'Suspendida':
        return 'red';
      case 'Vencida':
        return 'gray';
      default:
        return 'blue';
    }
  };

  const filteredLicencias = (Array.isArray(licencias) ? licencias : []).filter((lic) => {
    const cumpleTexto = lic.folioLicencia?.toLowerCase().includes(filtroTexto.toLowerCase()) ||
      lic.ciudadano?.toLowerCase().includes(filtroTexto.toLowerCase()) ||
      lic.nombreEstablecimiento?.toLowerCase().includes(filtroTexto.toLowerCase());
    
    const fechaLic = lic.creadoEn?.split('T')[0];
    const cumpleFecha = (!fechaDesde || fechaLic >= fechaDesde) && (!fechaHasta || fechaLic <= fechaHasta);
    
    const cumpleTipo = !filtroTipo || lic.tipo?.toLowerCase().includes(filtroTipo.toLowerCase());
    
    const cumpleEstatus = !filtroEstatus || lic.estatus === filtroEstatus;
    
    return cumpleTexto && cumpleFecha && cumpleTipo && cumpleEstatus;
  });

  const sortedLicencias = [...filteredLicencias].sort((a, b) => {
    if (!sortConfig) {
      const fechaA = a.creadoEn || '';
      const fechaB = b.creadoEn || '';
      return fechaB.localeCompare(fechaA);
    }
    
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortConfig.direction === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });

  const totalPaginas = Math.ceil(sortedLicencias.length / registrosPorPagina);
  const indiceInicio = (paginaActual - 1) * registrosPorPagina;
  const indiceFin = indiceInicio + registrosPorPagina;
  const licenciasPaginadas = sortedLicencias.slice(indiceInicio, indiceFin);

  const handleNuevaLicencia = () => {
    setModoModal('create');
    setLicenciaSeleccionada(null);
    onModalOpen();
  };

  const handleVerLicencia = (licencia: LicenciaVinosLicores) => {
    setModoModal('view');
    setLicenciaSeleccionada(licencia);
    onModalOpen();
  };

  const handleEditarLicencia = (licencia: LicenciaVinosLicores) => {
    setModoModal('edit');
    setLicenciaSeleccionada(licencia);
    onModalOpen();
  };

  const generarReportePDF = () => {
    const fecha = new Date();
    const fechaTexto = `${fecha.getDate().toString().padStart(2, '0')}/${(fecha.getMonth() + 1).toString().padStart(2, '0')}/${fecha.getFullYear()}`;
    
    let rangoFechas = '';
    if (fechaDesde && fechaHasta) {
      rangoFechas = `${formatearFecha(fechaDesde)} - ${formatearFecha(fechaHasta)}`;
    } else if (sortedLicencias.length > 0) {
      const fechasValidas = sortedLicencias
        .map(lic => lic.creadoEn?.split('T')[0])
        .filter(Boolean)
        .sort();
      
      if (fechasValidas.length > 0) {
        const fechaMasAntigua = fechasValidas[0];
        const fechaMasReciente = fechasValidas[fechasValidas.length - 1];
        rangoFechas = fechaMasAntigua === fechaMasReciente 
          ? formatearFecha(fechaMasAntigua) 
          : `${formatearFecha(fechaMasAntigua)} - ${formatearFecha(fechaMasReciente)}`;
      } else {
        rangoFechas = 'Sin fechas disponibles';
      }
    } else {
      rangoFechas = 'Sin datos';
    }
    
    const totalLicencias = sortedLicencias.length;
    const totalMonto = sortedLicencias.reduce((sum, lic) => sum + (Number(lic.montoLicencia) || 0), 0);
    
    const licenciasOrdenadas = [...sortedLicencias].sort((a, b) => {
      const fechaA = a.creadoEn || '';
      const fechaB = b.creadoEn || '';
      return fechaB.localeCompare(fechaA);
    });
    
    const filas = licenciasOrdenadas.map(lic => `
      <tr>
        <td style="text-align: center;">${lic.folioLicencia ?? lic.Id}</td>
        <td>${lic.ciudadano ?? '—'}</td>
        <td>${lic.nombreEstablecimiento ?? '—'}</td>
        <td>${lic.giroVinosLicores?.descripcion ?? '—'}</td>
        <td>${lic.tipo ?? '—'}</td>
        <td style="text-align: center;">${lic.creadoEn?.split('T')[0] ?? '—'}</td>
        <td style="text-align: right;">$${Number(lic.montoLicencia || 0).toFixed(2)}</td>
      </tr>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html lang="es">
        <head>
          <meta charset="UTF-8">
          <title>Reporte de Licencias de Vinos y Licores</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 15px; font-size: 12px; }
            .header { margin-bottom: 25px; padding-bottom: 15px; border-bottom: 3px solid #5b182b; }
            .title { color: #5b182b; font-size: 20px; font-weight: bold; margin: 0 0 5px 0; }
            .subtitle { color: #666; font-size: 14px; margin: 0 0 8px 0; }
            .date-range { color: #bb8c52; font-size: 16px; font-weight: bold; margin: 0; }
            .statistics { background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0; }
            .data-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 10px; }
            .data-table th, .data-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .data-table th { background-color: #5b182b; color: white; font-weight: bold; text-align: center; }
            .data-table tbody tr:nth-child(even) { background-color: #f8f9fa; }
            .footer { text-align: center; margin-top: 25px; padding-top: 15px; border-top: 1px solid #dee2e6; font-size: 10px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">Reporte de Licencias de Vinos y Licores</h1>
            <h2 class="subtitle">H. Ayuntamiento de Totolac, Tlaxcala</h2>
            <h3 class="date-range">Periodo: ${rangoFechas}</h3>
          </div>
          
          <div class="statistics">
            <strong>Total de Licencias:</strong> ${totalLicencias} | 
            <strong>Monto Total:</strong> $${Number(totalMonto).toFixed(2)}
          </div>
          
          <table class="data-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Ciudadano</th>
                <th>Establecimiento</th>
                <th>Giro</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Monto</th>
              </tr>
            </thead>
            <tbody>
              ${filas}
            </tbody>
          </table>
          
          <div class="footer">
            <p><strong>H. Ayuntamiento de Totolac</strong></p>
            <p>Desarrollo Económico - Tlaxcala, México</p>
            <p>Documento generado automáticamente el ${fechaTexto}</p>
          </div>
          
          <script>
            window.onload = function() {
              window.focus();
              setTimeout(() => { window.print(); }, 1000);
            };
          </script>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=1200,height=800');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  const imprimirLicencia = (lic: LicenciaVinosLicores) => {
    const fecha = lic.creadoEn?.split('T')[0] ?? '';
    const [anio, mes, dia] = fecha.split('-');

    const html = `
      <html>
        <head>
          <title>Licencia de Vinos y Licores</title>
          <style>
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
            }
            body {
              position: relative;
              width: 21cm;
              height: 27.9cm;
              margin: 0;
              font-family: Arial, sans-serif;
              font-size: 12pt;
            }
            .campo {
              position: absolute;
              text-transform: uppercase;
            }
            #giro { top: 130px; left: 1px; }
            #tipo { top: 210px; left: 1px; }
            #anio { top: 300px; left: 1px; }
            #ciudadano { top: 120px; left: 470px; }
            #nombreEstablecimiento { top: 200px; left: 550px; }
            #direccion { top: 240px; left: 480px; }
            #horario { top: 310px; left: 470px; }
            #fecha1 { top: 365px; left: 280px; }
            #fecha2 { top: 365px; left: 400px; }
            #fecha3 { top: 365px; left: 600px; }
            #folio { top: 80px; left: 600px; }
            #estatus { top: 160px; left: 50px; }
          </style>
        </head>
        <body>
          <div id="giro" class="campo">${lic.giroVinosLicores?.descripcion ?? ''}</div>
          <div id="tipo" class="campo">${lic.tipo ?? 'Temporal'}</div>
          <div id="anio" class="campo">${anio ?? ''}</div>
          <div id="ciudadano" class="campo">${lic.ciudadano ?? ''}</div>
          <div id="nombreEstablecimiento" class="campo">${lic.nombreEstablecimiento ?? ''}</div>
          <div id="direccion" class="campo">${lic.direccion ?? ''}</div>
          <div id="horario" class="campo">${lic.horario ?? '9:00 am - 10:00 pm'}</div>
          <div id="fecha1" class="campo">${dia}</div>
          <div id="fecha2" class="campo">${mes}</div>
          <div id="fecha3" class="campo">${anio}</div>
          <div id="folio" class="campo">${lic.folioLicencia ?? lic.Id}</div>
          <div id="estatus" class="campo">${lic.estatus ?? 'Pendiente'}</div>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    const ventana = window.open('', '_blank', 'width=800,height=600');
    if (ventana) {
      ventana.document.write(html);
      ventana.document.close();
    }
  };

  const generarReporteExcel = async () => {
    const XLSX = await import('xlsx');
    
    const licenciasOrdenadas = [...sortedLicencias].sort((a, b) => {
      const fechaA = a.creadoEn || '';
      const fechaB = b.creadoEn || '';
      return fechaB.localeCompare(fechaA);
    });
    
    const data = licenciasOrdenadas.map(lic => ({
      'Folio': lic.folioLicencia ?? lic.Id,
      'Ciudadano': lic.ciudadano ?? '',
      'Establecimiento': lic.nombreEstablecimiento ?? '',
      'Giro': lic.giroVinosLicores?.descripcion ?? '',
      'Tipo': lic.tipo ?? '',
      'Estatus': lic.estatus ?? '',
      'Dirección': lic.direccion ?? '',
      'Fecha': formatearFecha(lic.creadoEn || ''),
      'Monto': lic.montoLicencia ?? 0
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    
    const colWidths = [
      { wch: 12 }, { wch: 30 }, { wch: 25 }, { wch: 20 }, 
      { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 15 }
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Licencias Vinos y Licores');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `licencias_vinos_licores_${fecha}.xlsx`);
  };

  return (
    <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white" mt={12} p={4}>
      <Box mb={4}>
        <Heading fontSize="md" mb={2}>Filtros de búsqueda</Heading>
        <InputGroup mb={3}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Buscar por folio, ciudadano o establecimiento"
            value={filtroTexto}
            onChange={(e) => setFiltroTexto(e.target.value)}
          />
        </InputGroup>
        
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Box>
            <Text fontSize="sm" mb={1}>Fecha desde:</Text>
            <Input
              type="date"
              size="sm"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb={1}>Fecha hasta:</Text>
            <Input
              type="date"
              size="sm"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb={1}>Tipo:</Text>
            <Select
              size="sm"
              placeholder="Todos los tipos"
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="Temporal">Temporal</option>
              <option value="Anual">Anual</option>
            </Select>
          </Box>
          <Box>
            <Text fontSize="sm" mb={1}>Estatus:</Text>
            <Select
              size="sm"
              placeholder="Todos los estatus"
              value={filtroEstatus}
              onChange={(e) => setFiltroEstatus(e.target.value)}
            >
              <option value="Activa">Activa</option>
              <option value="Pendiente">Pendiente</option>
              <option value="Suspendida">Suspendida</option>
              <option value="Vencida">Vencida</option>
            </Select>
          </Box>
        </SimpleGrid>
        
        <Flex justify="space-between" mt={3}>
          <Button
            size="sm"
            bg="#bb8c52"
            color="white"
            _hover={{ bg: "#a67a47" }}
            onClick={() => {
              setFiltroTexto('');
              setFechaDesde('');
              setFechaHasta('');
              setFiltroTipo('');
              setFiltroEstatus('');
              setPaginaActual(1);
            }}
          >
            Limpiar Filtros
          </Button>
          <Flex gap={2}>
            <Button
              size="sm"
              bg="#5b182b"
              color="white"
              _hover={{ bg: "#691f30" }}
              leftIcon={<Icon as={FaFilePdf} />}
              onClick={generarReportePDF}
            >
              PDF
            </Button>
            <Button
              size="sm"
              bg="#681a2d"
              color="white"
              _hover={{ bg: "#5b182b" }}
              leftIcon={<Icon as={FaFileExcel} />}
              onClick={generarReporteExcel}
            >
              Excel
            </Button>
          </Flex>
        </Flex>
      </Box>

      <Table variant="simple" size="sm">
        <Thead bg="#5b182b">
          <Tr>
            <Th color="white" cursor="pointer" onClick={() => requestSort('folioLicencia')}>
              Folio {getSortIcon('folioLicencia')}
            </Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('ciudadano')}>
              Ciudadano {getSortIcon('ciudadano')}
            </Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('nombreEstablecimiento')}>
              Establecimiento {getSortIcon('nombreEstablecimiento')}
            </Th>
            <Th color="white">Giro</Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('tipo')}>
              Tipo {getSortIcon('tipo')}
            </Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('estatus')}>
              Estatus {getSortIcon('estatus')}
            </Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('creadoEn')}>
              Fecha {getSortIcon('creadoEn')}
            </Th>
            <Th color="white" cursor="pointer" onClick={() => requestSort('montoLicencia')}>
              Monto {getSortIcon('montoLicencia')}
            </Th>
            <Th color="white" textAlign="center">Acciones</Th>
          </Tr>
        </Thead>

        <Tbody>
          {licenciasPaginadas.map((lic) => (
            <Tr key={lic.Id}>
              <Td>{lic.folioLicencia ?? lic.Id}</Td>
              <Td>{lic.ciudadano ?? '—'}</Td>
              <Td>{lic.nombreEstablecimiento ?? '—'}</Td>
              <Td>{lic.giroVinosLicores?.descripcion ?? '—'}</Td>
              <Td>{lic.tipo ?? '—'}</Td>
              <Td>
                <Badge colorScheme={getEstatusColor(lic.estatus || 'Pendiente')}>
                  {lic.estatus ?? 'Pendiente'}
                </Badge>
              </Td>
              <Td>{formatearFecha(lic.creadoEn || '')}</Td>
              <Td isNumeric>${Number(lic.montoLicencia || 0).toFixed(2)}</Td>
              <Td>
                <Flex justify="center" gap={2}>
                  <Button 
                    size="xs" 
                    bg="#691f30" 
                    color="white" 
                    _hover={{ bg: "#5b182b" }} 
                    onClick={() => handleVerLicencia(lic)}
                  >
                    Ver
                  </Button>
                  <Button 
                    size="xs" 
                    bg="#bb8c52" 
                    color="white" 
                    _hover={{ bg: "#a67a47" }} 
                    onClick={() => handleEditarLicencia(lic)}
                  >
                    Editar
                  </Button>
                  <Button
                    size="xs"
                    bg="#681a2d"
                    color="white"
                    _hover={{ bg: "#5b182b" }}
                    onClick={() => {
                      setLicenciaIdHistorial(lic.Id);
                      onHistorialOpen();
                    }}
                  >
                    <Icon as={FaClock} />
                  </Button>
                  <Button 
                    size="xs" 
                    bg="#5b182b" 
                    color="white" 
                    _hover={{ bg: "#691f30" }}
                    onClick={() => imprimirLicencia(lic)}
                  >
                    <Icon as={FaPrint} />
                  </Button>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Flex justify="space-between" align="center" mt={4}>
        <Text fontSize="sm" color="#691f30">
          Mostrando {indiceInicio + 1} - {Math.min(indiceFin, sortedLicencias.length)} de {sortedLicencias.length} registros
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            bg="#bb8c52"
            color="white"
            _hover={{ bg: "#a67a47" }}
            isDisabled={paginaActual === 1}
            onClick={() => setPaginaActual(paginaActual - 1)}
          >
            Anterior
          </Button>
          {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((pagina) => (
            <Button
              key={pagina}
              size="sm"
              bg={paginaActual === pagina ? "#5b182b" : "white"}
              color={paginaActual === pagina ? "white" : "#5b182b"}
              border="1px"
              borderColor="#5b182b"
              _hover={{ bg: paginaActual === pagina ? "#691f30" : "#f7f7f7" }}
              onClick={() => setPaginaActual(pagina)}
            >
              {pagina}
            </Button>
          ))}
          <Button
            size="sm"
            bg="#bb8c52"
            color="white"
            _hover={{ bg: "#a67a47" }}
            isDisabled={paginaActual === totalPaginas}
            onClick={() => setPaginaActual(paginaActual + 1)}
          >
            Siguiente
          </Button>
        </Flex>
      </Flex>

      <ModalLicenciaVinosLicores
        isOpen={isModalOpen}
        onClose={onModalClose}
        licencia={licenciaSeleccionada}
        modo={modoModal}
        onSave={() => {
          const fetchLicencias = async () => {
            try {
              const res = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores`);
              const data = await res.json();
              setLicencias(Array.isArray(data) ? data : []);
            } catch (error) {
              console.error('Error al recargar licencias:', error);
              setLicencias([]);
            }
          };
          fetchLicencias();
        }}
      />

      <ModalHistorialVinosLicores
        isOpen={isHistorialOpen}
        onClose={onHistorialClose}
        licenciaId={licenciaIdHistorial}
      />

      <FloatingAddButton text='Nueva Licencia' onClick={handleNuevaLicencia} />
    </Box>
  );
};

export default TablaLicenciasVinosLicores;